import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Film, Play, X } from "lucide-react";
import { useLang } from "../context/LangContext";
import { videosAPI } from "../api";

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;

interface VideoItem {
  id: number;
  title: string;
  description?: string | null;
  video_url: string | null;
  thumbnail_url: string | null;
  order_index: number;
}

const isExternal = (url: string | null) => !!url && /^https?:\/\//i.test(url);

const toEmbedUrl = (url: string) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    }
    const v = u.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    return url;
  } catch {
    return url;
  }
};

const VideosPage: React.FC = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<VideoItem | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    videosAPI
      .getAll()
      .then((res) => setItems(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: "rgba(10,14,26,.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--border)",
          height: 52,
        }}>
        <div
          style={{
            maxWidth: 620,
            margin: "0 auto",
            padding: "0 16px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
            }}>
            <ArrowLeft size={16} /> {t("back")}
          </button>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            {t("videosPageTitle")}
          </span>
        </div>
      </div>

      <div
        style={{ maxWidth: 620, margin: "0 auto", padding: "68px 16px 32px" }}>
        {loading ?
          <p style={{ color: "var(--muted)", fontSize: 13, textAlign: "center" }}>
            {t("loading")}
          </p>
        : items.length === 0 ?
          <div
            style={{
              textAlign: "center",
              padding: "60px 16px",
              color: "var(--muted)",
            }}>
            <Film size={36} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
            <p style={{ fontSize: 14 }}>{t("noVideos")}</p>
          </div>
        : <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: 14,
            }}>
            {items.map((v) => (
              <div
                key={v.id}
                onClick={() => setActive(v)}
                className="fade-up"
                style={{
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 14,
                  overflow: "hidden",
                  cursor: "pointer",
                }}>
                <div
                  style={{
                    width: "100%",
                    height: 100,
                    background: "var(--card2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}>
                  {v.thumbnail_url ?
                    <img
                      src={`${IMG_BASE}/${v.thumbnail_url}`}
                      alt={v.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  : <Film size={24} color="var(--muted)" />}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(0,0,0,.25)",
                    }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        background: "rgba(59,130,246,.9)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}>
                      <Play size={16} color="#fff" fill="#fff" />
                    </div>
                  </div>
                </div>
                <div style={{ padding: 10 }}>
                  <p
                    style={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 12.5,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}>
                    {v.title}
                  </p>
                </div>
              </div>
            ))}
          </div>
        }
      </div>

      {active && (
        <div
          onClick={() => setActive(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.85)",
            zIndex: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 720 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}>
              <p style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>
                {active.title}
              </p>
              <button
                onClick={() => setActive(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                }}>
                <X size={20} />
              </button>
            </div>
            <div
              style={{
                position: "relative",
                paddingTop: "56.25%",
                background: "#000",
                borderRadius: 12,
                overflow: "hidden",
              }}>
              {active.video_url && isExternal(active.video_url) ?
                <iframe
                  src={toEmbedUrl(active.video_url)}
                  title={active.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              : active.video_url ?
                <video
                  controls
                  autoPlay
                  src={`${IMG_BASE}/${active.video_url}`}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                  }}
                />
              : <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--muted)",
                  }}>
                  {t("noVideos")}
                </div>
              }
            </div>
            {active.description && (
              <p
                style={{
                  color: "var(--muted)",
                  fontSize: 13,
                  marginTop: 12,
                  lineHeight: 1.5,
                }}>
                {active.description}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideosPage;
